import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { WithdrawPresenter } from './withdraw-presenter'
import { DashboardSummary, OpportunityDetails, Withdrawal, WithdrawalStatus } from '../../lib/types'
import { useDispatch, useSelector } from 'react-redux'
import { dashboardServices, projectServices, withdrawalServices } from '../../services'
import { RootState } from '../../store'
import './withdraw.scss'
import { showBanner } from '../../global-state/banner-slice'
import { captureEvent } from '../../lib/utils/posthogUtils/usePostHog'
import { EVENTS } from '../../lib/utils/constants'
import { PLATFORM_WITHDRAWAL_FEE } from './constants'

const PLACE_REDEMPTION_STEP = 2

const WithdrawContainer = () => {
  const navigate = useNavigate()
  const params = useParams()
  const dispatch = useDispatch()
  const { state } = useLocation()
  const { projectId, projectSlug } = params
  const { authToken, userId } = useSelector((state: RootState) => state.investor)
  const [opportunity, setOpportunity] = useState<OpportunityDetails>()
  const [currentStep, setCurrentStep] = useState(1)
  const [withdrawalAmount, setWithdrawalAmount] = useState<string>(state?.withdrawalAmount ?? '0')
  const [minWithdrawalAmount, setMinWithdrawalAmount] = useState<string>('0')
  const [maxWithdrawalAmount, setMaxWithdrawalAmount] = useState<string>('0')
  const [investmentSummary, setInvestmentSummary] = useState<DashboardSummary>({
    totalInvestedAmount: 0,
    totalReturnsTillDate: 0,
    realizedGains: 0,
    unrealizedGains: 0,
    realizedGainsAmount: 0,
    unrealizedGainsAmount: 0,
  })
  const [withdrawalRequest, setWithdrawalRequest] = useState<{ withdrawalRequestId: string }>({
    withdrawalRequestId: '',
  })
  const [activeWithdrawalRequest, setActiveWithdrawalRequest] = useState<Withdrawal>(
    {} as Withdrawal,
  )

  const getProjectById = async () => {
    const data = await projectServices.getOpportunityById(projectId as string, {
      Authorization: authToken ? authToken : '',
    })
    if (data && data.data && data.status === 200) {
      const ticketSize = parseInt(data.data?.ticketSize ?? '0') / 100
      setOpportunity(data.data)
      setMinWithdrawalAmount(String(ticketSize))
    } else if (data && data.data && data.status === 400) {
      dispatch(
        showBanner({
          text: 'That opportunity does not exist',
          variant: 'error-banner',
        }),
      )

      navigate('/dashboard')
    }
  }

  // Fetch active withdrawal request
  const getUserActiveWithdrawalRequest = async () => {
    const response = await withdrawalServices.getActiveWithdrawalRequest(
      String(userId),
      String(projectId),
      {
        Authorization: authToken ? authToken : '',
      },
    )
    if (response?.data && response?.data?.withdrawal && response?.status === 200) {
      // Filter out settled withdrawal requests
      if (
        ![
          WithdrawalStatus.CANCELLED,
          WithdrawalStatus.REJECTED,
          WithdrawalStatus.TRADE_EXECUTED,
        ].includes(response?.data?.withdrawal?.status)
      ) {
        setActiveWithdrawalRequest(response?.data.withdrawal)

        // Resume from saved progress; clamp for legacy 3-step metadata
        if (response?.data?.withdrawal?.metadata) {
          const resumedStep = Number(response.data?.withdrawal?.metadata?.completedSteps) + 1
          setCurrentStep(Math.min(Math.max(resumedStep, 1), PLACE_REDEMPTION_STEP))
        }

        const withdrawalAmount = response?.data?.withdrawal?.amount
        // Update withdrawal amount
        setWithdrawalAmount(
          withdrawalAmount ? String(Number(withdrawalAmount) / 100) : state?.withdrawalAmount,
        )
      }
    }
  }

  const getDashboardInvestmentSummary = async () => {
    const data = await dashboardServices.getInvestmentSummary(String(projectId), {
      Authorization: authToken ? authToken : '',
    })
    if (data && data.data && data.status === 200) {
      setInvestmentSummary(data?.data)
    }
  }

  const getMaxWithdrawalAmount = async () => {
    const data = await withdrawalServices.getMaxWithdrawalAmountByOpportunity(projectId as string, {
      Authorization: authToken ? authToken : '',
    })
    if (data.status === 200 && Number(data?.data?.maxAmount) === 0) {
      dispatch(
        showBanner({
          text: 'You do not have any units allocated for that opportunity',
          variant: 'warning-banner',
        }),
      )

      navigate('/dashboard')
    } else if (data && data.data && data.status === 200) {
      setMaxWithdrawalAmount(String((data?.data?.maxAmount / 100).toFixed(2)))
    }
  }

  const postWithdrawalAmount = async (payload: any) => {
    const response = await withdrawalServices.postOpportunityWithdrawal(payload, {
      Authorization: authToken ? authToken : '',
    })
    if (response.status === 200) {
      setWithdrawalRequest({
        withdrawalRequestId: String(response?.data?.id),
      })
      setCurrentStep((prev) => prev + 1)
    } else {
      const message = response.data.message || 'Error saving the redemption request'
      dispatch(showBanner({ text: message, variant: 'error-banner' }))
    }
  }

  const patchWithdrawalRequest = async (withdrawalId: number, payload: any): Promise<boolean> => {
    if (!withdrawalId) return false
    const response = await withdrawalServices.patchOpportunityWithdrawal(withdrawalId, payload, {
      Authorization: authToken ? authToken : '',
    })
    if (response.status === 200) {
      if (currentStep < PLACE_REDEMPTION_STEP) {
        setCurrentStep((prev) => prev + 1)
        return true
      }

      captureEvent(EVENTS.withdrawal_order_placed, { response })
      return true
    }

    const message = response.data.message || 'Error updating the redemption request'
    dispatch(showBanner({ text: message, variant: 'error-banner' }))
    return false
  }

  useEffect(() => {
    if (projectId !== undefined && projectSlug !== undefined) {
      getMaxWithdrawalAmount()
      getProjectById()
      getUserActiveWithdrawalRequest()
      getDashboardInvestmentSummary()
    } else navigate('/dashboard')
  }, [])

  // Changing minimum withdrawal amount
  useEffect(() => {
    const ticketSize = parseInt(opportunity?.ticketSize ?? '0') / 100
    if (Number(maxWithdrawalAmount) <= ticketSize) {
      setMinWithdrawalAmount(maxWithdrawalAmount)
    } else {
      setMinWithdrawalAmount(String(ticketSize))
    }
  }, [maxWithdrawalAmount, opportunity])

  const handleSubmitWithdrawalAmount = async () => {
    const roundedAmount = Math.floor(Number(withdrawalAmount) * 10) / 10
    postWithdrawalAmount({
      opportunityId: opportunity?.id,
      investorId: userId,
      withdrawalAmount: Math.round(roundedAmount * 100),
      metadata: { completedSteps: 1 },
    })
  }

  const handlePlaceWithdrawalRequest = async (): Promise<boolean> => {
    const withdrawalRequestId = activeWithdrawalRequest?.id ?? withdrawalRequest.withdrawalRequestId
    return patchWithdrawalRequest(withdrawalRequestId, {
      metadata: {
        completedSteps: PLACE_REDEMPTION_STEP,
        receivedAmount: (Number(withdrawalAmount) - PLATFORM_WITHDRAWAL_FEE) * 100,
      },
    })
  }

  const handleWithdrawalPlacedSuccess = () => {
    navigate(`/dashboard/investments/${projectId}`)
  }

  const handleDisableButton = (): boolean => {
    if (currentStep === 1) {
      return (
        Number(withdrawalAmount) > Number(maxWithdrawalAmount) ||
        Number(withdrawalAmount) < Number(minWithdrawalAmount)
      )
    }

    return true
  }

  const backToInvestmentHandler = () => navigate(`/dashboard/investments/${opportunity?.id}`)

  return (
    <div className='withdrawal-container'>
      <WithdrawPresenter
        backToInvestmentHandler={backToInvestmentHandler}
        opportunity={opportunity!}
        currentStep={currentStep}
        investmentSummary={investmentSummary}
        withdrawalAmount={withdrawalAmount}
        minWithdrawalAmount={minWithdrawalAmount}
        maxWithdrawalAmount={maxWithdrawalAmount}
        setWithdrawalAmount={setWithdrawalAmount}
        handleDisableButton={handleDisableButton}
        handleSubmitWithdrawalAmount={handleSubmitWithdrawalAmount}
        handlePlaceWithdrawalRequest={handlePlaceWithdrawalRequest}
        onWithdrawalPlacedSuccess={handleWithdrawalPlacedSuccess}
      />
    </div>
  )
}

export { WithdrawContainer }
